<script setup lang="ts">
import { computed } from "vue";

import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuSelect from "~/components/dumb/DropdownMenuSelect.vue";
import GenericSubdropdown from "~/components/dumb/GenericSubdropdown.vue";
import Template from "~/components/dumb/Template.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { getLocalAgentModelSettings, type LocalAgent } from "~/constants/localAgent";
import {
  getThinkingLevelDisplayName,
  getThinkingLevelMenuTitle,
  MODEL_TO_AVAILABLE_THINKING_LEVELS,
  MODELS_SUPPORTING_THINKING,
} from "~/constants/model";
import { ThinkingIcon } from "~/icons";
import { AiModel, AiThinkingLevel, DropdownMenuItemKind, Placement } from "~/shared/enums";
import type { DropdownMenuSection } from "~/shared/types";

const props = defineProps<{
  currentModel: AiModel;
  currentThinkingLevel: AiThinkingLevel;
  onThinkingLevelSelect: (level: AiThinkingLevel) => void;
  localAgent?: LocalAgent;
  isItem?: boolean;
}>();

const localAgentModelSettings = computed(() =>
  props.localAgent !== undefined
    ? getLocalAgentModelSettings(props.localAgent, props.currentModel, props.currentThinkingLevel)
    : null
);
const availableLevels = computed(
  () =>
    localAgentModelSettings.value?.thinkingLevelOptions ??
    MODEL_TO_AVAILABLE_THINKING_LEVELS.get(props.currentModel) ??
    []
);
const isThinkingSupported = computed(() =>
  props.localAgent !== undefined
    ? availableLevels.value.some((level) => level !== AiThinkingLevel.NONE)
    : MODELS_SUPPORTING_THINKING.has(props.currentModel)
);

const sections = computed<DropdownMenuSection[]>(() => {
  const levels = availableLevels.value;
  return [
    {
      title: "Thinking level",
      items: levels.map((level, i) => ({
        title: getThinkingLevelMenuTitle({ level, index: i, levels, model: props.currentModel }),
        kind: DropdownMenuItemKind.BUTTON,
        disabled: props.currentThinkingLevel === level,
        onClick: () => props.onThinkingLevelSelect(level),
      })),
    },
  ];
});

const displayName = computed(() =>
  getThinkingLevelDisplayName({ level: props.currentThinkingLevel, model: props.currentModel })
);

const tooltipText = computed(() => "This model does not support thinking.");
</script>

<template>
  <div
    v-if="isItem"
    class="w-full border-t border-md md:rounded-b md:border-x md:border-t-0 md:border-b md:bg-lt md:py-1 md:app-drag-none"
    :class="{ 'md:hover:bg-md': isThinkingSupported }">
    <component :is="isThinkingSupported ? Template : Tooltip" :text="tooltipText" :placement="Placement.BOTTOM">
      <GenericSubdropdown
        :class="{ 'pointer-events-none': !isThinkingSupported }"
        title="Thinking level"
        :icon="ThinkingIcon"
        :width-pixels="200"
        :right-text="displayName"
        right-text-style="text-xs text-vlt font-normal"
        :sections="sections"
        :disabled="!isThinkingSupported" />
    </component>
  </div>
  <Tooltip v-else :disabled="isThinkingSupported" :text="tooltipText" :placement="Placement.BOTTOM">
    <DropdownMenu
      :sections="sections"
      :disabled="!isThinkingSupported"
      :placement="Placement.BOTTOM_LEFT"
      :width-pixels="200"
      cover>
      <DropdownMenuSelect :text="displayName" :disabled="!isThinkingSupported" class="w-48" />
    </DropdownMenu>
  </Tooltip>
</template>
